import { FC } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { useExamplePreview } from 'hooks/useExamplePreview';

import { FieldTableHeader } from 'components/_basic/Form/FieldTable2/components/FieldTableHeader';
import { FieldTableTotalRow } from 'components/_basic/Form/FieldTable2/components/FieldTableTotalRow';
import { FieldTableContainer } from 'components/_basic/Form/FieldTableContainer';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { ILiabilitiesAndEquityBodyProps } from './types';
import { DividerComponent } from '../../../../../components/DividerComponent/DividerComponent';
import { TableSectionTitle } from '../../../../../components/TableComponents/TableSectionTitle';
import { FIRST_COLUMN_WIDTH, liabilitiesAndEquityPrefix } from '../../../../utils/form';
import { LIABILITIES_AND_EQUITY_FORM_NAMES, useGetLiabilityAndEquityTableHeadings } from '../../utils/form';
import { CurrentLiabilitiesTable } from '../CurrentLiabilitiesTable';
import { EquityTable } from '../EquityTable';
import { NonCurrentLiabilitiesTable } from '../NonCurrentLiabilitiesTable';

import styles from './LiabilitiesAndEquityBody.module.scss';

export const LiabilitiesAndEquityBody: FC<ILiabilitiesAndEquityBodyProps> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const period = useWatch({ name: GLOBAL_PERIOD_NAME });
  const { isExamplePage} = useExamplePreview()

  const columnsNames = getPeriodColumnsNames(period);
  const { totalLiabilitiesAndEquity} = useGetLiabilityAndEquityTableHeadings(isExamplePage);

  const totalName = concatWithDot(
    liabilitiesAndEquityPrefix,
    LIABILITIES_AND_EQUITY_FORM_NAMES.TOTAL_LIABILITIES_AND_EQUITY,
  );

  return (
    <FormSectionFilledCard>
      <FieldTableContainer>
        <Grid item xs className={styles.stickyTop}>
          <FieldTableHeader
            name={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
            disabled={disabled}
            columnsNames={columnsNames || []}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            editableColumn={true}
            firstColumnClass={styles.sticky}
          />
        </Grid>
        <Grid container direction="column" gap={4}>
          <Grid item xs>
            <Grid item xs>
              <TableSectionTitle
                title={t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.title')}
              />
            </Grid>
            <Grid item xs>
              <CurrentLiabilitiesTable disabled={disabled} />
            </Grid>
          </Grid>
          <Grid item xs>
            <Grid item xs>
              <TableSectionTitle
                title={t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.title')}
              />
            </Grid>
            <Grid item xs>
              <NonCurrentLiabilitiesTable disabled={disabled} />
            </Grid>
          </Grid>
          <Grid item xs>
            <Grid item xs>
              <TableSectionTitle title={t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.title')} />
            </Grid>
            <Grid item xs>
              <EquityTable disabled={disabled} />
            </Grid>
          </Grid>
          <Grid item xs>
            <DividerComponent />
          </Grid>
          <Grid item xs>
            <FieldTableTotalRow
              totalName={totalName}
              columns={columnsNames || []}
              disabled={disabled}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              totalRowTitle={totalLiabilitiesAndEquity}
            />
          </Grid>
        </Grid>
      </FieldTableContainer>
    </FormSectionFilledCard>
  );
};
